import type { ReactElement } from "react";
import { Chip } from "@kundeninfo/ui/me-design";
import { fileSizeText } from "../../format.js";
import { KiKnopf } from "../meHuellen.js";
import { ReadOnlyMarker } from "./ReadOnlyMarker.js";
import { RESULT_STATUS_KIND, isTooLarge, resultName, resultText, rowRank } from "./upload.js";
import type { UploadRow } from "./upload.js";
import { DOCUMENT_FILE_TEXT as T } from "./texts.js";
import "./documentFiles.css";

/**
 * Die Dateien eines Uploads, je Datei Größe bzw. Ergebnis — EIN Baustein für das Panel „Dateien hochladen“ und das Panel
 * der historischen Vorgänge (Aufgabe 26). Das Schlechteste oben (Hausregel): abgewiesene und zu große zuerst, dann die
 * Doubletten (mit „Vorhandene Datei verknüpfen“, an einer nur lesbaren Datei — 518 — statt dessen „nur lesend“), dann
 * die abgelegten, zuletzt die noch nicht gesendeten. Der Rückruf
 * bekommt den Index der Zeile in `rows`, nicht den der Anzeige.
 */
export function UploadResultList(p: {
  rows: readonly UploadRow[];
  label: string;
  busy: boolean;
  onLinkExisting: (index: number, fileId: string, name: string) => void;
}): ReactElement | null {
  if (p.rows.length === 0) return null;
  const ordered = p.rows.map((row, index) => ({ row, index })).sort((a, b) => rowRank(a.row) - rowRank(b.row));
  return (
    <ul className="doc-files__results" aria-label={p.label}>
      {ordered.map(({ row: { file, result }, index }) => (
        <li key={`${String(index)}:${file.name}`} className="doc-files__result" data-status={result?.status ?? "pending"}>
          {result === null ? file.name : resultName(result)}{" "}
          {result !== null ? <Chip statusArt={RESULT_STATUS_KIND[result.status]}>{resultText(result)}</Chip>
            : isTooLarge(file) ? <Chip statusArt="fehler">{T.tooLarge}</Chip>
              : <span className="doc-files__muted">{fileSizeText(file.size)}</span>}
          {result?.status === "duplicate" && (
            <>
              {" "}
              {result.current.readOnly ? <ReadOnlyMarker /> : (
                <KiKnopf kompakt deaktiviert={p.busy}
                  onClick={() => p.onLinkExisting(index, result.current.id, result.name)}>
                  {T.linkExisting}
                </KiKnopf>
              )}
            </>
          )}
        </li>
      ))}
    </ul>
  );
}

/**
 * Die Ablagezeile „Ablage: ‹Pfad›“ — mit dem leisen „wird beim Hochladen angelegt“, solange es den Ordner nicht gibt.
 * Ohne Pfad (das historische Panel kennt ihn erst mit Datum und alter Nummer) steht `pending` leise da.
 */
export function StorageLine(p: { path: readonly string[] | null; willCreate: boolean; pending?: string }): ReactElement {
  return (
    <p className="doc-files__storage">
      {p.path === null ? <span className="doc-files__muted">{p.pending}</span> : T.storage(p.path)}
      {p.path !== null && p.willCreate && <> <span className="doc-files__muted">{T.willCreate}</span></>}
    </p>
  );
}
